import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { LabelPrintFailureDialogData } from '../error-mesage.actions';

@Component({
  selector: 'daas-consulting-shared-label-print-failure-dialog',
  templateUrl: './label-print-failure-dialog.component.html',
  styleUrl: './label-print-failure-dialog.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabelPrintFailureDialogComponent implements OnDestroy {
  readonly data: LabelPrintFailureDialogData;

  readonly preview$: Observable<string | null>;
  readonly isLoading$: Observable<boolean>;
  readonly previewError$: Observable<string | null>;

  readonly #dialogRef: MatDialogRef<LabelPrintFailureDialogComponent>;
  readonly #preview$ = new BehaviorSubject<string | null>(null);
  readonly #isLoading$ = new BehaviorSubject<boolean>(false);
  readonly #previewError$ = new BehaviorSubject<string | null>(null);
  readonly #destroy$ = new Subject<void>();
  #objectUrl: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: LabelPrintFailureDialogData,
    dialogRef: MatDialogRef<LabelPrintFailureDialogComponent>
  ) {
    this.data = data;
    this.#dialogRef = dialogRef;
    this.preview$ = this.#preview$.asObservable();
    this.isLoading$ = this.#isLoading$.asObservable();
    this.previewError$ = this.#previewError$.asObservable();
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
    this._revokeObjectUrl();
  }

  handleShowPreview(): void {
    if (this.#isLoading$.value || this.#preview$.value !== null) {
      return;
    }

    this.#isLoading$.next(true);
    this.#previewError$.next(null);
    // `takeUntil` y no `take(1)`: el pedido ya emite una sola vez y completa,
    // lo que hace falta es cortarlo si el dialogo se cierra mientras carga. Sin
    // esto la respuesta llega sobre un componente ya destruido y crea un object
    // URL que nadie revoca, porque `ngOnDestroy` ya paso.
    this.data
      .loadPreview()
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: (blob) => {
          this._revokeObjectUrl();
          this.#objectUrl = URL.createObjectURL(blob);
          this.#preview$.next(this.#objectUrl);
          this.#isLoading$.next(false);
        },
        error: () => {
          this.#previewError$.next(
            'No se pudo generar la vista previa del rótulo.'
          );
          this.#isLoading$.next(false);
        },
      });
  }

  handleClose(): void {
    this.#dialogRef.close();
  }

  private _revokeObjectUrl(): void {
    if (this.#objectUrl !== null) {
      URL.revokeObjectURL(this.#objectUrl);
      this.#objectUrl = null;
    }
  }
}
